import { AutocompleteState, BaseItem } from "@algolia/autocomplete-core";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { isEmpty } from "lodash-es";
import { ReactNode, useEffect } from "react";

import { AutocompleteActions } from "@/components/Search/SearchBar/types/searchBar.types";
import { KeyNames } from "@/utils/enums";

type AutocompleteItemProps = {
    href?: string;
    query?: string;
    getRedirectUrl?: () => string | undefined;
    onSubmit?: () => void;
    className?: string;
    itemId?: number | null;
    autocompleteState?: AutocompleteState<BaseItem>;
    autocompleteActions?: AutocompleteActions;
    children: ReactNode;
};

const AutocompleteItem = ({
    href,
    query,
    getRedirectUrl,
    onSubmit,
    className,
    children,
    autocompleteState,
    autocompleteActions,
    itemId
}: AutocompleteItemProps) => {
    const active =
        isDefined(itemId) && autocompleteState?.activeItemId === itemId;

    const redirectUrl = getRedirectUrl?.();

    const submitQuery = () => {
        if (redirectUrl) {
            location.assign(redirectUrl);
            return;
        }

        if (!query) return;
        autocompleteActions?.setQuery(query);
        autocompleteActions?.submit(query);
    };

    const submitHref = () => {
        if (!href) return;
        window.location.href = href;
    };

    const handleOnKeyDown = (event: KeyboardEvent) => {
        if (event.key !== KeyNames.ENTER) return;
        event.stopPropagation();
        event.preventDefault();

        if (!active) return;

        if (redirectUrl) {
            location.assign(redirectUrl);
            return;
        }

        onSubmit?.();
        submitQuery();
        submitHref();
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        document.addEventListener("keydown", handleOnKeyDown);
        return () => document.removeEventListener("keydown", handleOnKeyDown);
    }, [autocompleteState?.activeItemId]);

    return isEmpty(href) ? (
        <div
            className={cn(
                "tw:cursor-pointer tw:p-2 tw:hover:bg-grey tw:transition-all",
                "tw:has-[.productTile]:p-0! tw:hover:has-[.productTile]:bg-transparent! tw:h-full",
                {
                    "tw:bg-grey tw:has-[.productTile]:bg-transparent! tw:group/productTile-active":
                        active
                },
                className
            )}
            onClick={submitQuery}
        >
            {children}
        </div>
    ) : (
        <a
            className={cn(
                "tw:cursor-pointer tw:p-2 tw:hover:bg-grey tw:transition-transform",
                "tw:has-[.productTile]:p-0! tw:hover:has-[.productTile]:bg-transparent! tw:h-full",
                {
                    "tw:bg-grey tw:has-[.productTile]:bg-transparent! tw:group/productTile-active":
                        active
                },
                className
            )}
            href={href}
        >
            {children}
        </a>
    );
};

export { AutocompleteItem };
